
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const stories = [
  {
    name: "Mitchell Wiggins",
    subtitle: "Jacksonville Hooters",
    detail: "United States Basketball League",
    highlight: "NBA Houston Rockets",
    image: "/lovable-uploads/01587f45-a38b-4cff-a0d1-c4699cb35a2e.png",
    alt: "Mitchell Wiggins - Jacksonville Hooters",
  },
  {
    name: "Jessie Hicks",
    subtitle: "Thomas Jefferson High School",
    detail: "Richmond, Virginia",
    highlight: "WNBA Connecticut Sun",
    image: "/lovable-uploads/65175aff-1416-4e68-8640-e9ef416f4730.png",
    alt: "Jessie Hicks - WNBA Connecticut Sun",
  },
  {
    name: "Samaria Smith",
    subtitle: "Senior, #21, Shooting Guard",
    detail: "5A All-Regional Selection",
    extraLines: [
      "2024-2025 Ridgeview H.S., Columbia, South Carolina",
      "RidgeView High School Female Player of the Year 2025",
    ],
    highlight: "Signed to Play College Basketball at Coker College",
    image: "/lovable-uploads/2a1f4265-6439-4997-be04-b8db5232bf65.png",
    alt: "Samaria Smith - All Regional Selection",
  },
  {
    name: "David Lewis",
    subtitle: "Lake Norman Charter",
    detail: "Boys Basketball 2025-2026",
    highlight: "Knight Award",
    image: "/lovable-uploads/david-lewis-knight-award.jpg",
    alt: "David Lewis - Lake Norman Charter Knight Award",
  },
  {
    name: "Crosby Holst",
    subtitle: "NorthPoint NightHawks 2025-2026",
    detail: "Middle School Boys Basketball MVP",
    extraLines: ["High School Mens Basketball Most Improved"],
    highlight: "Future Star Award",
    image: "/lovable-uploads/crosby-holst-awards.jpg",
    alt: "Crosby Holst - NorthPoint NightHawks Award Winner",
  },
  {
    name: "Jurnee Darling",
    subtitle: "Signed to Cleveland Community College",
    detail: "Women's Basketball",
    highlight: "Committed to Play College Basketball",
    image: "/lovable-uploads/jurnee-darling-signing.jpg",
    alt: "Jurnee Darling - Cleveland Community College Signing",
  },

];

const SamariaSuccessStory = () => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent(index);
      setIsTransitioning(false);
    }, 300);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % stories.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + stories.length) % stories.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const story = stories[current];

  return (
    <section id="success-stories" className="py-6 sm:py-8 md:py-12 bg-gray-50 w-full">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Success Stories</h2>
          <p className="text-xs sm:text-sm text-gray-600 max-w-xl mx-auto">
            Celebrating our athletes' incredible achievements and college commitments.
          </p>
        </div>

        {/* Featured Main Photo */}
        <div className="max-w-md mx-auto mb-6 sm:mb-8">
          <div className="rounded-xl overflow-hidden shadow-lg border-4 border-primary/20">
            <img
              src="/lovable-uploads/jurnee-darling-cleveland-main.jpg"
              alt="Jurnee Darling signs with Cleveland Community College"
              className="w-full h-auto object-cover"
            />
          </div>
          <p className="text-center mt-3 text-sm sm:text-base font-semibold text-gray-800">
            Jurnee Darling — Signed to Cleveland Community College
          </p>
        </div>


        
        <div className="max-w-4xl mx-auto relative">
          {/* Navigation Arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all hover:scale-110"
            aria-label="Previous story"
          >
            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-2 transition-all hover:scale-110"
            aria-label="Next story"
          >
            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </button>

          {/* Card */}
          <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <Card className="overflow-hidden shadow-md mx-8 sm:mx-12">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0">
                    <img 
                      src={story.image} 
                      alt={story.alt} 
                      className="w-full h-full object-cover rounded-lg shadow-md"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{story.name}</h3>
                    <p className="text-sm font-semibold text-gray-700">{story.subtitle}</p>
                    <p className="text-sm text-gray-600">{story.detail}</p>
                    {story.extraLines?.map((line, i) => (
                      <p key={i} className="text-xs text-gray-600">{line}</p>
                    ))}
                    <div className="mt-2 px-3 py-1.5 bg-primary/10 rounded-md inline-block">
                      <p className="text-sm font-bold text-primary">{story.highlight}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {stories.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === current ? 'bg-primary scale-110' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to story ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SamariaSuccessStory;
